import {
  DeliveryTemplate,
  SampleSetTemplate,
} from "@/services/backend/delivery-templates/delivery";
import {
  PutDeliveryTemplateRequest,
  useGetDeliveryTemplateQuery,
  usePutDeliveryTemplateMutation,
} from "@/services/backend/delivery-templates/service";
import {
  Testnorm,
  ProcedureType,
  SampleType,
  SamplingMethod,
} from "@/services/backend/types";
import { AirSamplerSelector } from "@/shared/components/domain/air-samplers/air-sampler-selector";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  testnorm,
  procedureTypes,
  samplingMethods,
} from "@/shared/components/enum-labels";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardFooter } from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Separator } from "@/shared/components/ui/separator";
import { Switch } from "@/shared/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { cn } from "@/shared/lib/utils";
import { createEntityId } from "@/shared/nidavellir/types/entity-id";
import { zodResolver } from "@hookform/resolvers/zod";
import { skipToken } from "@reduxjs/toolkit/query";
import { Plus } from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";

const SampleSetTemplateSchema = z.object({
  templateId: z.string(),
  sampleSetNumber: z.string().min(1, "Eine Probensetnummer wird benötigt"),
  samplingMethod: z.nativeEnum(SamplingMethod),
  procedureType: z.nativeEnum(ProcedureType),
  comment: z.string().optional(),
});

const DeliveryTemplateSchema = z
  .object({
    templateId: z.string(),
    customerId: z.string(),
    description: z.string().min(1, "Eine Beschreibung wird benötigt"),
    templateDescription: z
      .string()
      .min(1, "Die Vorlage benötigt eine Beschreibung"),
    sampleSets: z
      .array(SampleSetTemplateSchema)
      .min(1, "Mindesten ein Probenset muss in einer Lieferung enthalten sein"),
    sampledBy: z.string(),
    sampleTakers: z.string({
      message: "Probenehmer werden benötigt",
    }),
    samplingVolume: z.string().optional(),
    testnorm: z.nativeEnum(Testnorm),
    airSamplerHoleCount: z.number().optional(),
    airSamplerDescription: z.string().optional(),
    airSamplerId: z.string().optional(),
    hasDifferentiation: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const isALK = data.sampleSets.some(
      (set) => set.samplingMethod === SamplingMethod.aLK,
    );
    if (isALK) {
      // Only validate required fields when ALK is selected in any sample set
      if (!data.airSamplerHoleCount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Eine Lochzahl wird benötigt",
          path: ["airSamplerHoleCount"],
        });
      }
      if (!data.airSamplerDescription) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Eine Beschreibung für den Luftkeimsammler wird benötigt",
          path: ["airSamplerDescription"],
        });
      }
      if (!data.airSamplerId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Eine ID für den Luftkeimsammler wird benötigt",
          path: ["airSamplerId"],
        });
      }
      if (!data.samplingVolume) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Ein Probevolumen für den Luftkeimsammler wird benötigt",
          path: ["samplingVolume"],
        });
      }
    }
  });

function normalizeDeliveryTemplate(templateData: DeliveryTemplate): FormData {
  const firstSampleSet = templateData.sampleSets?.[0];
  const firstWithAirSampler = templateData.sampleSets?.find(
    (s) => s.airSampler,
  );

  // Für sampledBy, sampleTakers, testnorm: AirSampler zuerst, sonst erster SampleSet
  const sourceSet = firstWithAirSampler ?? firstSampleSet;

  return {
    templateId: templateData.templateId,
    customerId: templateData.customerId,
    description: templateData.description,
    templateDescription: templateData.templateDescription,
    sampleSets:
      templateData.sampleSets?.map((s: SampleSetTemplate) => ({
        templateId: s.templateId,
        sampleSetNumber: s.sampleSetNumber ?? "",
        comment: s.comment ?? "",
        samplingMethod: s.samplingMethod ?? SamplingMethod.aLK,
        procedureType: s.procedureType ?? ProcedureType.RLT2N,
      })) ?? [],
    sampledBy: sourceSet?.sampledBy ?? "",
    sampleTakers: sourceSet?.sampleTakers?.join(", ") ?? "",
    testnorm: sourceSet?.testnorm as Testnorm,
    hasDifferentiation: sourceSet?.hasDifferentiation ?? false,
    samplingVolume:
      firstWithAirSampler?.samplingVolume?.toString() ?? undefined,
    airSamplerHoleCount:
      firstWithAirSampler?.airSampler?.holeCount ?? undefined,
    airSamplerDescription:
      firstWithAirSampler?.airSampler?.description ?? undefined,
    airSamplerId: firstWithAirSampler?.airSampler?.originalId ?? undefined,
  };
}

type FormData = z.infer<typeof DeliveryTemplateSchema>;

interface DeliveryTemplateModalProps {
  templateId?: string;
  customerId: string;
  buttonLabel?: string;
  className?: string;
}

export function DeliveryTemplateModal({
  templateId,
  customerId,
  buttonLabel = templateId
    ? "Lieferungsvorlage bearbeiten"
    : "Lieferungsvorlage anlegen",
  className = "w-52",
}: DeliveryTemplateModalProps): ReactElement {
  const { data: templateData } = useGetDeliveryTemplateQuery(
    templateId ? { id: templateId } : skipToken,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [putDeliveryTemplate, { error }] = usePutDeliveryTemplateMutation();
  const form = useForm<FormData>({
    resolver: zodResolver(DeliveryTemplateSchema),
    defaultValues: {
      templateId: createEntityId()!.toString(),
      customerId,
      description: "",
      templateDescription: "",
      sampleSets: [],
      sampledBy: "",
      sampleTakers: "",
      testnorm: Testnorm.VDI,
      hasDifferentiation: false,
    },
  });

  useEffect(() => {
    if (!templateData) return;
    const normalized = normalizeDeliveryTemplate(templateData);
    const parsed = DeliveryTemplateSchema.parse(normalized);
    form.reset(parsed);
  }, [templateData, form]);

  const onSubmit = (data: FormData) => {
    const request: PutDeliveryTemplateRequest = {
      deliveryTemplate: {
        templateId: data.templateId.toString(),
        customerId: data.customerId,
        description: data.description,
        templateDescription: data.templateDescription,
        sampleSets: data.sampleSets.map((set): SampleSetTemplate => {
          const newSet: SampleSetTemplate = {
            templateId: set.templateId
              ? set.templateId
              : createEntityId()!.toString(),
            sampleSetNumber: set.sampleSetNumber,
            comment: set.comment ? set.comment : undefined,
            samplingMethod: set.samplingMethod,
            sampleType:
              set.samplingMethod === SamplingMethod.AK
                ? SampleType.SurfaceMicrobial
                : SampleType.AirMicrobial,
            procedureType: set.procedureType,
            sampledBy: data.sampledBy,
            samplingVolume: data.samplingVolume
              ? Number(data.samplingVolume)
              : undefined,
            testnorm: data.testnorm,
            sampleTakers: [data.sampleTakers],
            airSampler: undefined,
            hasDifferentiation:
              set.samplingMethod === SamplingMethod.aLK
                ? data.hasDifferentiation
                : false,
          };

          // Only include airSampler data when ALK sampling method is used
          if (set.samplingMethod === SamplingMethod.aLK) {
            newSet.airSampler = {
              id: data.airSamplerId!,
              originalId: data.airSamplerId!,
              description: data.airSamplerDescription!,
              holeCount: data.airSamplerHoleCount!,
            };
          }

          return newSet;
        }),
      },
    };

    putDeliveryTemplate(request);
    setIsOpen(false);
    form.reset({
      templateId: createEntityId()!.toString(), // Generate new ID
      customerId,
      description: "",
      templateDescription: "",
      hasDifferentiation: false,
      sampleSets: [], // Reset arrays
    });
  };

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger className={cn(className)} asChild>
        <Button variant={templateId ? "outline" : "default"}>
          {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[425px] md:max-w-[700px] lg:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>{buttonLabel}</DialogTitle>
          <DialogDescription>
            Hier können neue Lieferungsvorlagen für die künftige Verwendung
            angelegt werden.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-1 flex-col"
          >
            <div className="mb-4 space-y-2">
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="templateDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beschreibung der Vorlage</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Beschreibung der Vorlage"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beschreibung</FormLabel>
                      <FormControl>
                        <Input placeholder="Beschreibung" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Tabs defaultValue="samples" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="samples">Proben</TabsTrigger>
                  <TabsTrigger value="metadata">Probenahmedaten</TabsTrigger>
                  <TabsTrigger value="active-air-microbial-metadata">
                    aLK
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="samples" className="mt-2">
                  <SampleSetsTab />
                </TabsContent>
                <TabsContent value="metadata" className="mt-2">
                  <SamplingMetadata />
                </TabsContent>
                <TabsContent
                  value="active-air-microbial-metadata"
                  className="mt-2"
                >
                  <ActiveMicrobialMetadataTab />
                </TabsContent>
              </Tabs>
            </div>
            {/* Buttons */}
            <div className="flex justify-between space-x-4 pt-4">
              <Button type="submit">Lieferung speichern</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function SampleSetsTab(): ReactElement {
  const methods = useFormContext();
  const {
    fields: sampleSetFields,
    append,
    remove,
  } = useFieldArray({
    control: methods.control,
    name: "sampleSets",
  });

  return (
    <Card className="flex-1 border-0 p-0 shadow-none">
      <CardContent className="p-0">
        <ScrollArea className="h-[300px] p-2 md:h-[300px]">
          {sampleSetFields.map((sampleSet, sampleSetIndex) => (
            <Card key={`${sampleSet.id}`} className="mb-3 shadow-none">
              <CardContent className="p-3">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.sampleSetNumber`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Probensetnummer</FormLabel>
                        <FormControl>
                          <Input placeholder="Probensetnummer" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.comment`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kommentar</FormLabel>
                        <FormControl>
                          <Input placeholder="Kommentar" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.samplingMethod`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Methode</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className="w-full"
                                defaultValue={field.value || SamplingMethod.aLK}
                              >
                                <SelectValue placeholder="Probentyp auswählen" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent
                              defaultValue={field.value || SamplingMethod.aLK}
                            >
                              {Object.values(SamplingMethod).map((value) => (
                                <SelectItem key={value} value={value}>
                                  {samplingMethods[value]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.procedureType`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Verfahren</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className="w-full"
                                defaultValue={
                                  field.value || ProcedureType.RLT2N
                                }
                              >
                                <SelectValue placeholder="Verfahren auswählen" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent
                              defaultValue={field.value || ProcedureType.RLT2N}
                            >
                              {Object.values(ProcedureType).map((value) => (
                                <SelectItem key={value} value={value}>
                                  {procedureTypes[value]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  className="mt-2"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(sampleSetIndex);
                  }}
                >
                  Löschen
                </Button>
              </CardContent>
            </Card>
          ))}
        </ScrollArea>
      </CardContent>
      <CardFooter className="flex flex-col items-start space-y-2 pt-2">
        <Separator />
        <Button
          className="w-full"
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({
              templateId: createEntityId()!.toString(),
              sampleSetNumber: "",
              samplingMethod: SamplingMethod.aLK,
              procedureType: ProcedureType.RLT2N,
              comment: "",
            })
          }
        >
          <Plus className="mr-1 h-3 w-3" /> Probenset hinzufügen
        </Button>
      </CardFooter>
    </Card>
  );
}

function SamplingMetadata(): ReactElement {
  const methods = useFormContext();
  return (
    <ScrollArea className="h-[300px] md:h-[300px]">
      <Card className="py-0 shadow-none">
        <CardContent className="flex flex-col space-y-3 p-3">
          <FormField
            control={methods.control}
            name="sampledBy"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Beprobung durch:</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Beprobung durch..."
                    type="text"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="sampleTakers"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Probenehmer</FormLabel>
                <FormControl>
                  <Input placeholder="Probenehmer" type="text" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="testnorm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Prüfgrundlage</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className="w-full"
                        defaultValue={field.value || Testnorm.VDI}
                      >
                        <SelectValue placeholder="Prüfgrundlage" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent defaultValue={field.value || Testnorm.VDI}>
                      {Object.values(Testnorm).map((value) => (
                        <SelectItem key={value} value={value}>
                          {testnorm[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </ScrollArea>
  );
}

function ActiveMicrobialMetadataTab(): ReactElement {
  const methods = useFormContext();
  return (
    <ScrollArea className="h-[300px] md:h-[300px]">
      <Card className="py-0 shadow-none">
        <CardContent className="flex flex-col space-y-3 p-3">
          <AirSamplerSelector
            onChange={(airSampler) => {
              methods.setValue("airSamplerHoleCount", airSampler.holeCount);
              methods.setValue("airSamplerDescription", airSampler.description);
              methods.setValue("airSamplerId", airSampler.originalId);
            }}
          />
          <FormField
            control={methods.control}
            name="airSamplerDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Beschreibung</FormLabel>
                <FormControl>
                  <Input
                    disabled
                    placeholder="Beschreibung"
                    type="text"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="airSamplerHoleCount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lochzahl</FormLabel>
                <FormControl>
                  <Input
                    disabled
                    placeholder="Lochzahl"
                    type="number"
                    min={0}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="samplingVolume"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Probevolumen</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Probevolumen"
                    type="number"
                    min={0}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="hasDifferentiation"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <FormLabel className="font-normal">Differenzierung</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </ScrollArea>
  );
}
